import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, IdCard, ShieldCheck, MapPin, User as UserIcon, Save, Lock, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const FIELDS = [
  ["name", "Full name", "text"],
  ["phone", "Phone", "text"],
  ["address_line1", "Address line 1", "text"],
  ["address_line2", "Address line 2", "text"],
  ["city", "City / Town", "text"],
  ["postcode", "Postcode", "text"],
  ["country", "Country", "text"],
  ["citizenship", "Citizenship", "text"],
  ["date_of_birth", "Date of birth", "date"],
];

export default function Profile() {
  const { user, refresh: refreshUser } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        address_line1: user.address_line1 || "",
        address_line2: user.address_line2 || "",
        city: user.city || "",
        postcode: user.postcode || "",
        country: user.country || "United Kingdom",
        citizenship: user.citizenship || "",
        date_of_birth: user.date_of_birth || "",
      });
    }
  }, [user]);

  if (!user) return null;

  const photoMissing = !user.photo_data_url;
  const completion = (() => {
    const required = ["name", "phone", "address_line1", "city", "postcode", "country", "citizenship"];
    const filled = required.filter((k) => (form[k] || "").trim().length > 0);
    const photoBonus = user.photo_data_url ? 1 : 0;
    return Math.round(((filled.length + photoBonus) / (required.length + 1)) * 100);
  })();

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/me/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      await refreshUser();
      void data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me", form);
      toast.success("Profile saved");
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-pearl min-h-[calc(100vh-5rem)] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <p className="eyebrow">Your account</p>
        <h1 className="mt-3 font-display text-4xl text-navy">My Profile</h1>

        {/* Header card */}
        <div className="mt-8 bg-white border border-navy/10 rounded-sm p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-12 gap-6 items-center">
          <div className="sm:col-span-3 flex flex-col items-center sm:items-start">
            <div
              className="relative w-32 h-32 rounded-full overflow-hidden bg-navy/5 border-2 border-gold/40 flex items-center justify-center"
              data-testid="profile-photo"
            >
              {user.photo_data_url ? (
                <img src={user.photo_data_url} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <UserIcon size={42} className="text-navy/30" />
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoChange} data-testid="profile-photo-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="profile-photo-upload"
              className="mt-3 inline-flex items-center gap-2 text-sm text-navy hover:text-saffron"
            >
              <Camera size={14} /> {uploading ? "Uploading…" : photoMissing ? "Add latest photo" : "Update photo"}
            </button>
          </div>
          <div className="sm:col-span-6">
            <p className="text-xs uppercase tracking-[0.22em] text-saffron font-semibold">{user.badge || "Community Member"}</p>
            <h2 className="mt-1 font-display text-3xl text-navy">{user.name}</h2>
            <p className="text-slate-600 text-sm">{user.email}</p>
            <p className="text-xs text-slate-500 mt-1">Member ID: <span className="font-mono text-navy">{user.membership_id || "—"}</span></p>
            <div className="mt-3 inline-flex items-center gap-2 text-xs text-navy/70">
              <ShieldCheck size={14} className={user.voting_eligible ? "text-saffron" : "text-slate-400"} />
              {user.voting_eligible ? "Eligible to vote at AGM" : "Not yet eligible to vote (paid member, 90+ days)"}
            </div>
          </div>
          <div className="sm:col-span-3 flex flex-col gap-2">
            <Link
              to="/profile/card"
              data-testid="view-membership-card-cta"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-pearl rounded-sm hover:bg-navy-light"
            >
              <IdCard size={16} /> Membership card
            </Link>
            <Link
              to="/membership"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-navy/20 text-navy rounded-sm hover:bg-navy/5"
            >
              Upgrade tier
            </Link>
            <div className="mt-2 text-xs text-slate-500">
              Profile complete: <span className="text-navy font-medium">{completion}%</span>
              <div className="mt-1 h-1.5 bg-navy/10 rounded-full overflow-hidden">
                <div className="h-full bg-gold transition-all" style={{ width: `${completion}%` }} />
              </div>
            </div>
          </div>
        </div>

        {photoMissing && (
          <div className="mt-4 p-4 bg-saffron/10 border border-saffron/30 rounded-sm text-sm text-navy" data-testid="photo-prompt">
            <strong>Please add your latest photo</strong>, it appears on your digital membership card and helps us verify members at events.
          </div>
        )}

        {/* Edit form */}
        <form onSubmit={onSave} className="mt-8 bg-white border border-navy/10 rounded-sm p-6 sm:p-8 space-y-5" data-testid="profile-edit-form">
          <div className="flex items-center gap-2 text-navy">
            <MapPin size={18} className="text-gold" />
            <h3 className="font-display text-2xl">Personal details</h3>
          </div>
          {FIELDS.map(([k, label, type]) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</span>
              <input
                type={type}
                value={form[k] ?? ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`profile-${k}`}
                className="mt-2 w-full border border-navy/15 px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
              />
            </label>
          ))}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              data-testid="profile-save"
              className="inline-flex items-center gap-2 px-7 py-3 bg-gold text-navy rounded-sm hover:bg-gold-light font-medium disabled:opacity-60"
            >
              <Save size={16} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>

      <ChangePasswordPanel />
    </section>
  );
}

// ── Change password panel ──────────────────────────────────────────────────
function ChangePasswordPanel() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  // Live strength meter
  const score = (() => {
    if (!next) return 0;
    let s = 0;
    if (next.length >= 8) s += 1;
    if (next.length >= 12) s += 1;
    if (/[A-Z]/.test(next) && /[a-z]/.test(next)) s += 1;
    if (/[0-9]/.test(next)) s += 1;
    if (/[^A-Za-z0-9]/.test(next)) s += 1;
    return s; // 0-5
  })();
  const strengthLabel = ["", "Very weak", "Weak", "Okay", "Strong", "Very strong"][score];
  const strengthColor = [
    "bg-navy/10",
    "bg-red-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-600",
  ][score];

  const validClient = next.length >= 8 && next === confirm && next !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (next !== confirm) return toast.error("New password and confirmation don't match.");
    if (next.length < 8) return toast.error("Password must be at least 8 characters.");
    if (next === current) return toast.error("New password must be different from the current one.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed. Use your new password next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 bg-white border border-navy/10 rounded-sm p-6 sm:p-8" data-testid="change-password-panel">
      <h2 className="font-display text-2xl text-navy inline-flex items-center gap-2">
        <Lock size={18} className="text-saffron" /> Change password
      </h2>
      <p className="mt-1 text-sm text-navy/55">
        For your security, choose a password you don't use anywhere else.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4 max-w-md">
        <PasswordField
          label="Current password" value={current} onChange={setCurrent}
          show={showCurrent} setShow={setShowCurrent}
          autoComplete="current-password"
          testid="cp-current"
        />
        <div>
          <PasswordField
            label="New password" value={next} onChange={setNext}
            show={showNext} setShow={setShowNext}
            autoComplete="new-password"
            testid="cp-new"
          />
          {next && (
            <div className="mt-2" data-testid="cp-strength">
              <div className="h-1.5 bg-navy/10 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${strengthColor}`}
                  style={{ width: `${(score / 5) * 100}%` }}
                />
              </div>
              <p className={`text-[11px] mt-1 ${score >= 4 ? "text-emerald-700" : score >= 2 ? "text-amber-700" : "text-red-700"}`}>
                {strengthLabel} · use 12+ chars with letters, numbers and a symbol for best strength
              </p>
            </div>
          )}
        </div>
        <PasswordField
          label="Confirm new password" value={confirm} onChange={setConfirm}
          show={showNext} setShow={setShowNext}
          autoComplete="new-password"
          testid="cp-confirm"
          mismatch={confirm.length > 0 && confirm !== next}
        />

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={busy || !validClient}
            data-testid="cp-submit"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-navy text-pearl rounded-sm hover:bg-navy-light font-medium disabled:opacity-50 transition-colors"
          >
            <Lock size={14} /> {busy ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, setShow, autoComplete, testid, mismatch }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-navy/50">{label}</span>
      <div className="mt-2 relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required
          data-testid={testid}
          className={`w-full border px-4 py-3 pr-11 rounded-sm focus:outline-none focus:border-saffron ${
            mismatch ? "border-red-400" : "border-navy/15"
          }`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy/70"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {mismatch && <p className="text-[11px] text-red-700 mt-1">Doesn't match the new password above.</p>}
    </label>
  );
}
